import { Alert, AlertTitle } from '@/components/UI/Alert';
import { Badge } from '@/components/UI/Badge';
import { Button, ButtonLink } from '@/components/UI/Button';
import { Card, CardContent } from '@/components/UI/Card';
import { Input } from '@/components/UI/Input';
import { Skeleton } from '@/components/UI/Skeleton/Skeleton';
import { EmptyState } from '@/components/UI/State';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/UI/Table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/UI/Tooltip';
import { Typography } from '@/components/UI/Typography';
import { cn } from '@/util/cn';
import { formatDateDisplay } from '@/util/formatter/formatDate';
import { AlertCircle, ArrowRight, Lock, Search } from 'lucide-react';
import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { useSupplierUpdateRecords } from '../hooks/queries/useSupplierUpdateRecords';
import {
  SupplierUpdateOutstandingSection,
  SupplierUpdateRecord
} from '../types/erpSync.types';
import { categoryChipColor } from '../utils/categoryChipColor';
import { SupplierNameCell } from './SupplierNameCell';

const COLUMN_COUNT = 6;
const SKELETON_ROW_KEYS = ['sk-row-0', 'sk-row-1', 'sk-row-2'];
const SKELETON_CELL_KEYS = Array.from({ length: COLUMN_COUNT }, (_, i) => `sk-cell-${i}`);
const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;
const MAX_VISIBLE_SECTIONS = 2;

/** Copy shown for a locked section's non-actionable affordance (REQ-047/REQ-078). Deliberately
 * names NO role/person/team — this product has no named approver roles; access is per-user
 * permission, so the only truthful thing to say is that the CALLER lacks it. */
const LOCKED_SECTION_HINT = 'You do not have permission to approve this section';

/** One outstanding-section pill: colour-coded and actionable for a section the caller can decide;
 * a dashed, non-interactive pill showing the CATEGORY NAME (REQ-047 — the category, not field-level
 * detail, must always be visible) with a lock icon for one the caller cannot act on. */
const OutstandingSectionPill: FC<{ section: SupplierUpdateOutstandingSection }> = ({
  section
}) =>
  section.callerCanDecide ? (
    <Badge variant="soft" color={categoryChipColor(section.categoryName)} size="sm">
      {section.categoryName}
    </Badge>
  ) : (
    <Badge
      variant="outlined"
      color="secondary"
      size="sm"
      className="border-dashed"
      title={LOCKED_SECTION_HINT}
      aria-label={`${section.categoryName}: ${LOCKED_SECTION_HINT}`}
    >
      <Lock aria-hidden="true" />
      {section.categoryName}
    </Badge>
  );

/** Outstanding-approvals column: every outstanding section is rendered (REQ-047 — locked sections
 * are collapsed/non-interactive, never hidden), capped at 2 visible pills with a "+N more" affordance. */
const OutstandingApprovalsCell: FC<{ record: SupplierUpdateRecord }> = ({ record }) => {
  // Sections the CALLER can decide sort first, so the 2-pill cap can never truncate away their own
  // outstanding action. Server order is otherwise arbitrary: a caller whose only decidable section
  // came back third saw two locked pills and "+1 more", making the row read as somebody else's
  // problem. REQ-047 requires the list to show what is outstanding, and CR §10 names approvals
  // stalling (rather than surfacing as an access error) as the risk.
  // Array#sort is stable, so ordering within each group is left as the server returned it.
  const sections = [...record.outstandingSections].sort(
    (a, b) => Number(b.callerCanDecide) - Number(a.callerCanDecide)
  );

  if (sections.length === 0) {
    return <span className="text-muted-foreground text-sm italic">(None)</span>;
  }

  const visible = sections.slice(0, MAX_VISIBLE_SECTIONS);
  const hidden = sections.slice(MAX_VISIBLE_SECTIONS);

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {visible.map((section) => (
        <OutstandingSectionPill key={section.categoryName} section={section} />
      ))}
      {hidden.length > 0 && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger className="cursor-default">
              <Badge variant="outlined" color="outline" size="sm">
                +{hidden.length} more
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              {/* Category names only — REQ-047 requires the category to stay visible, but no
                  approver role/person/team is ever named (this product has none). */}
              {hidden.map((section) => section.categoryName).join(', ')}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};

const StatusPill: FC<{ status: SupplierUpdateRecord['status'] }> = ({ status }) => (
  <Badge variant="soft" color={status === 'Unapproved' ? 'warning' : 'success'} size="sm">
    {status}
  </Badge>
);

/*
 * The "Needs re-check" pill (Bug #7477) was REMOVED here by #7662.
 *
 * It rendered `PendingApprovalRowDto.VerificationBlockedCategories`, which
 * `GetPendingApprovalsHandler.IsVerificationBlocked` derives from the `SupplierVerificationStatuses`
 * projection — no row for a verification-enabled category means blocked. That projection is never
 * populated for this journey (Compliance publishes on one of the three paths that complete a report,
 * and the reviewer's browser calling Compliance directly is not one of them), so the count equalled
 * "every verification-enabled pending category" on every row, permanently.
 *
 * Approval no longer depends on it. #7210 moved the gate client-side, onto the Compliance report the
 * review card reads, so the pill told an approver a row could not be approved and the card then
 * offered Approve — the same contradiction #7662 fixed on the card, one screen earlier. Removing the
 * claim is the class fix; leaving it would have shipped a list that argues with the screen it opens.
 *
 * Not replaced with a client-side equivalent: this list would have to fetch one Compliance report per
 * row to know, which is an N-report fan-out on a paged list (DSP-1) for a hint the card states
 * accurately on arrival.
 *
 * The server field and `IsVerificationBlocked` are deliberately LEFT IN PLACE and unread, exactly as
 * `LocalProjectionAccountVerificationGate` and `_verificationGate` were, so restoring server-side
 * enforcement re-instates this pill without a contract change.
 */

/**
 * Feature #7210 / L7 fix: renders the denormalised `supplierCompanyName` the server now joins
 * onto every record (no extra request) when present. `SupplierNameCell` — the per-row
 * Company-service lookup this replaces — is kept ONLY as the fallback for a row whose name is
 * still null (a link seeded before the name existed), so older rows resolve a name instead of
 * showing a bare id, without reintroducing a request for every row that already has a name.
 */
const SupplierCell: FC<{ record: SupplierUpdateRecord }> = ({ record }) =>
  record.supplierCompanyName ? (
    <span className="font-medium text-slate-900">{record.supplierCompanyName}</span>
  ) : (
    <SupplierNameCell supplierCompanyId={record.supplierCompanyId} />
  );

/**
 * Pending supplier updates dashboard (Feature #7210 / S17), rebuilt over the persistent,
 * server-side searched/filtered/paged `supplier-update-records` read model (S1/S14) instead of
 * the open-bundles-only `pending-approvals` endpoint. Columns: Supplier | Outstanding approvals |
 * Status | Submitted | Changes | Review|View — REQ-037/039/041.
 */
export const PendingApprovalsInbox: FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [outstandingSection, setOutstandingSection] = useState('');
  const [page, setPage] = useState(1);
  const [knownSections, setKnownSections] = useState<string[]>([]);

  // REQ-081: server-side search — debounced so every keystroke doesn't fire a request.
  //
  // The mount run is skipped deliberately. Effects run on mount, so this armed a timer at load that
  // fired setPage(1) ~300ms later even though nothing had been typed. That is not harmless: a user
  // who clicked "Next" inside that window had their click applied first and was then silently
  // bounced back to page 1 by the settling timer. On mount there is also nothing to apply — `search`
  // already equals searchInput.trim() ('') — so the run is pure downside.
  const hasSearchInputChanged = useRef(false);
  useEffect(() => {
    if (!hasSearchInputChanged.current) {
      hasSearchInputChanged.current = true;
      return;
    }

    const handle = setTimeout(() => {
      setSearch(searchInput.trim());
      // Resetting to page 1 is correct HERE: the result set just changed, so the old page number is
      // meaningless. It is only wrong when the search itself did not change.
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data, isLoading, isError, refetch } = useSupplierUpdateRecords({
    search: search || undefined,
    outstandingSection: outstandingSection || undefined,
    page,
    pageSize: PAGE_SIZE
  });

  // The `usePendingApprovalsList` cross-reference that used to live here went with the pill (#7662).
  // It existed only to read `verificationBlockedCategories` off the open-bundles read model, so its
  // removal also drops a second, 60-second-polling query this screen no longer has a use for.

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const currentPage = data?.page ?? page;

  // The backend has no distinct-section-names lookup, so the filter's option list is built up
  // from every outstanding section name observed across fetched pages/filters, accumulating rather
  // than being replaced by the (possibly narrower) current filtered view.
  useEffect(() => {
    if (rows.length === 0) {
      return;
    }
    setKnownSections((prev) => {
      const names = new Set(prev);
      rows.forEach((row) =>
        row.outstandingSections.forEach((s) => names.add(s.categoryName))
      );
      return Array.from(names).sort();
    });
  }, [rows]);

  const isEmpty = !isLoading && !isError && rows.length === 0;

  const rangeStart = totalCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, totalCount);

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <div className="mx-auto max-w-[90rem]">
      <Card>
        <CardContent className="pt-6">
          <Typography variant="h5" className="mb-2">
            Pending supplier updates
          </Typography>
          <Typography variant="body2" className="text-muted-foreground mb-4">
            Review supplier profile changes waiting for your approval before they reach
            your ERP.
          </Typography>

          <div className="mb-4 flex flex-wrap items-center gap-3">
            <Input
              icon={<Search className="text-muted-foreground h-4 w-4" />}
              placeholder="Search by supplier name"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="max-w-xs"
            />
            <select
              aria-label="Filter by outstanding section"
              value={outstandingSection}
              onChange={(e) => {
                setOutstandingSection(e.target.value);
                setPage(1);
              }}
              className="border-input h-9 rounded-md border bg-white px-3 py-1 text-sm shadow-sm"
            >
              <option value="">All sections</option>
              {knownSections.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          {isError && (
            <Alert color="destructive" variant="outlined">
              <AlertCircle className="size-4" />
              <AlertTitle>
                Failed to load pending supplier updates.
                <div className="mt-2">
                  <Button variant="outlined" size="sm" onClick={() => refetch()}>
                    Retry
                  </Button>
                </div>
              </AlertTitle>
            </Alert>
          )}

          {isEmpty && (
            <EmptyState
              icon={<Search />}
              title="No supplier updates match your search."
            />
          )}

          {!isError && !isEmpty && (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Outstanding approvals</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Changes</TableHead>
                    <TableHead align="center" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading
                    ? SKELETON_ROW_KEYS.map((rowKey) => (
                        <TableRow key={rowKey}>
                          {SKELETON_CELL_KEYS.map((cellKey) => (
                            <TableCell key={cellKey}>
                              <Skeleton className="h-4 w-24" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    : rows.map((row) => (
                        <TableRow key={row.supplierCompanyId}>
                          <TableCell>
                            <SupplierCell record={row} />
                          </TableCell>
                          <TableCell>
                            <OutstandingApprovalsCell record={row} />
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap items-center gap-1.5">
                              <StatusPill status={row.status} />
                            </div>
                          </TableCell>
                          <TableCell>{formatDateDisplay(row.submittedAt)}</TableCell>
                          <TableCell>{row.changeCount}</TableCell>
                          <TableCell align="center">
                            <ButtonLink
                              to={`/pending-approvals/${row.supplierCompanyId}`}
                              variant="text"
                              size="sm"
                            >
                              {row.status === 'Unapproved' ? 'Review' : 'View'}
                              <ArrowRight className="h-4 w-4" />
                            </ButtonLink>
                          </TableCell>
                        </TableRow>
                      ))}
                </TableBody>
              </Table>

              {!isLoading && (
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                  <Typography variant="body2" className="text-muted-foreground">
                    Showing {rangeStart}&ndash;{rangeEnd} of {totalCount}
                  </Typography>
                  <nav aria-label="pagination" className="flex items-center gap-1">
                    <Button
                      variant="outlined"
                      size="sm"
                      disabled={currentPage <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Prev
                    </Button>
                    {pageNumbers.map((pageNumber) => (
                      <Button
                        key={pageNumber}
                        variant={pageNumber === currentPage ? 'solid' : 'text'}
                        size="sm"
                        className={cn('size-9 rounded-full p-0')}
                        onClick={() => setPage(pageNumber)}
                      >
                        {pageNumber}
                      </Button>
                    ))}
                    <Button
                      variant="outlined"
                      size="sm"
                      disabled={currentPage >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    >
                      Next
                    </Button>
                  </nav>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
